import { useState, useRef, useCallback, useEffect } from 'react'
import { useUploadDocument } from '../../api/documents'
import { useProjectsList } from '../../api/projects'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card } from '../ui/Card'
import { cn } from '../../lib/utils'

const ACCEPTED_EXTENSIONS =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip,.rar,.7z'

const typeLabels: Record<string, string> = {
  contract: 'Договор',
  invoice: 'Счёт',
  act: 'Акт',
  specification: 'ТЗ',
  other: 'Другое',
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType: string): React.ReactNode {
  if (mimeType.startsWith('image/')) {
    return (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    )
  }
  if (mimeType === 'application/pdf') {
    return (
      <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    )
  }
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

interface DocumentUploadModalProps {
  isOpen: boolean
  onClose: () => void
  projectId?: string
}

export function DocumentUploadModal({ isOpen, onClose, projectId }: DocumentUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadDocument = useUploadDocument()
  const { data: projects } = useProjectsList()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadData, setUploadData] = useState({
    name: '',
    type: 'other',
    projectId: projectId || '',
    description: '',
  })

  // Sync projectId prop when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      setUploadData((prev) => ({ ...prev, projectId }))
    }
  }, [isOpen, projectId])

  const resetForm = useCallback(() => {
    setSelectedFile(null)
    setUploadData({ name: '', type: 'other', projectId: projectId || '', description: '' })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [projectId])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      if (!uploadData.name) {
        setUploadData((prev) => ({ ...prev, name: file.name }))
      }
    }
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      const file = e.dataTransfer.files?.[0]
      if (file) {
        setSelectedFile(file)
        if (!uploadData.name) {
          setUploadData((prev) => ({ ...prev, name: file.name }))
        }
      }
    },
    [uploadData.name]
  )

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) return

    await uploadDocument.mutateAsync({
      file: selectedFile,
      name: uploadData.name || selectedFile.name,
      type: uploadData.type,
      projectId: uploadData.projectId || undefined,
      description: uploadData.description || undefined,
    })

    resetForm()
    onClose()
  }

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <Card className="w-full max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-6">Загрузить документ</h2>

          <form onSubmit={handleUpload} className="space-y-4">
            {/* File Drop Zone */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Файл
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                  selectedFile
                    ? 'border-accent-lime bg-accent-lime/5'
                    : isDragOver
                      ? 'border-accent-lime/70 bg-accent-lime/10'
                      : 'border-border-medium hover:border-accent-lime/50'
                )}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-bg-tertiary flex items-center justify-center text-text-tertiary">
                      {getFileIcon(selectedFile.type)}
                    </div>
                    <div className="text-left">
                      <p className="font-medium truncate max-w-[200px]">{selectedFile.name}</p>
                      <p className="text-xs text-text-tertiary">{formatFileSize(selectedFile.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <svg
                      className="w-8 h-8 mx-auto mb-2 text-text-tertiary"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p className="text-sm text-text-secondary">
                      Нажмите или перетащите файл
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept={ACCEPTED_EXTENSIONS}
              />
            </div>

            {/* Name */}
            <Input
              label="Название"
              value={uploadData.name}
              onChange={(e) => setUploadData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Название документа"
            />

            {/* Type & Project */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">
                  Тип
                </label>
                <select
                  value={uploadData.type}
                  onChange={(e) => setUploadData((prev) => ({ ...prev, type: e.target.value }))}
                  className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                >
                  {Object.entries(typeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">
                  Проект
                </label>
                <select
                  value={uploadData.projectId}
                  onChange={(e) => setUploadData((prev) => ({ ...prev, projectId: e.target.value }))}
                  className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                >
                  <option value="">Без проекта</option>
                  {projects?.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Описание
              </label>
              <textarea
                value={uploadData.description}
                onChange={(e) => setUploadData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 resize-none"
                rows={2}
                placeholder="Описание документа..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={handleClose}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                disabled={uploadDocument.isPending || !selectedFile}
              >
                {uploadDocument.isPending ? 'Загрузка...' : 'Загрузить'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  )
}
